import React, {useCallback} from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import {
  ARIA_DRAWER_WIDTH_CSS_VAR,
  type RouteClaim,
  type ShellRouter,
  type ShellLocation,
} from '@wandb/forge-shell-contract';
import {AriaProvider, useAriaContext} from './aria/AriaContext';

import {toShellAccount} from './account/accountModel';
import {useAccountContext, AccountProvider} from './account/AccountProvider';
import {UrlAccountBroadcastProvider} from './account/UrlAccountBroadcastContext';
import {
  activeRouteClaim,
  remotePathname,
  shellRouteClaimTable,
  type ActiveRouteClaim,
  type RemoteRouteClaims,
} from './routeClaims';
import {useAnalyticsBootstrap} from './analytics/useAnalyticsBootstrap';
import {usePageViews} from './analytics/usePageViews';
import {currentProductForPath, productForRemote} from './header/forgeProducts';
import {ShellHeaderProvider} from './header/ShellHeaderContext';
import {ShellHeader} from './header/ShellHeader';
import {HomePage} from './home/HomePage';
import {remoteLocation, shellPath} from './location';
import {RemoteMount} from './RemoteMount';
import {loadRemoteMount} from './remoteLoader';
import {resolveSurface, type Surface} from './surface';
import {RequireOnboarded} from './RequireOnboarded';
import {RequireViewer} from './RequireViewer';
import {ColorSchemeSync} from './theme/useColorScheme';
import styles from './App.module.css';
import {useViewer} from './identity/useViewer';
import {CLILoginRoutes} from './cliLogin/CLILoginRoutes';
import {CLI_LOGIN_PATHS} from './cliLogin/paths';
import {IdentityRedirect} from './identity/IdentityRedirect';
import {ShellErrorBoundary} from './ShellErrorBoundary';
import {ErrorState} from '@wandb/components';
import {ShellLoader} from './ShellLoader';

const IdentitySurface = React.lazy(() =>
  import('./identity/IdentitySurface').then(module => ({
    default: module.IdentitySurface,
  }))
);

const RemotePage = ({
  remote,
  routeClaim,
}: {
  remote: RemoteRouteClaims;
  routeClaim: RouteClaim;
}) => {
  const {selectedAccount, isLoading, selectShellAccount} = useAccountContext();
  const aria = useAriaContext();
  const browserLocation = useLocation();
  const navigate = useNavigate();
  const config = window.__FORGE_SHELL_CONFIG__?.remotes?.[remote.name];
  const location: ShellLocation = remoteLocation(
    routeClaim,
    browserLocation.pathname,
    browserLocation.search,
    browserLocation.hash,
    browserLocation.state
  );
  const router: ShellRouter = {
    /*
      Kept for remotes that predate `routeClaims` and only know how to prefix. A
      remote that routeClaims no root has no mount to prefix with, so it gets the
      routeClaim it is being served at — wrong for its other routeClaims, but those are
      exactly the URLs `routeClaims` and `createHref` exist to get right.
    */
    basePath: remote.basePath ?? routeClaim.shellPath,
    activeProductId: currentProductForPath(
      browserLocation.pathname,
      shellRouteClaimTable()
    )?.id,
    routeClaims: remote.routeClaims,
    location,
    createHref(to) {
      return shellPath(remote.routeClaims, to);
    },
    navigate(to, options) {
      navigate(shellPath(remote.routeClaims, to), {
        replace: options?.replace,
        state: options?.state,
      });
    },
    go(delta) {
      window.history.go(delta);
    },
  };

  return (
    <RemoteMount
      remote={config}
      remoteName={remote.name}
      label={productForRemote(remote.name).label}
      load={loadRemoteMount}
      router={router}
      origins={window.__FORGE_SHELL_CONFIG__?.origins}
      next={`${browserLocation.pathname}${browserLocation.search}${browserLocation.hash}`}
      selectedAccount={toShellAccount(isLoading, selectedAccount)}
      selectAccount={selectShellAccount}
      aria={aria}
    />
  );
};

const goBack = () => window.history.back();

const ShellNotFound = () => {
  const navigate = useNavigate();
  const goHome = useCallback(() => navigate('/'), [navigate]);
  return (
    <div className={styles.notFound}>
      <meta name="robots" content="noindex" />
      <ErrorState
        variant="404"
        secondaryActionLabel="Go back"
        onPrimaryAction={goHome}
        onSecondaryAction={goBack}
      />
    </div>
  );
};

const ClaimedPage = ({active}: {active: ActiveRouteClaim}) => {
  const browserLocation = useLocation();
  // A claimed remote path has one canonical shell URL, so the same page reached
  // through the mount is straightened out client-side.
  const canonicalPath = shellPath(
    active.remote.routeClaims,
    remotePathname(active.routeClaim, browserLocation.pathname)
  );

  if (canonicalPath !== browserLocation.pathname) {
    return (
      <Navigate
        replace
        to={`${canonicalPath}${browserLocation.search}${browserLocation.hash}`}
        state={browserLocation.state}
      />
    );
  }

  /*
    Keyed by remote rather than rendered by the matched route, so moving between
    two paths the same remote routeClaims keeps its mount — and its in-memory state —
    alive.
  */
  return (
    <RemotePage
      key={active.remote.name}
      remote={active.remote}
      routeClaim={active.routeClaim}
    />
  );
};

const ARIA_INSET_STYLE: React.CSSProperties = {
  marginRight: `var(${ARIA_DRAWER_WIDTH_CSS_VAR}, 0px)`,
};

const ForgeSurface = ({
  origins,
}: {
  origins?: Partial<Record<Surface, string>>;
}) => {
  const browserLocation = useLocation();
  const active = activeRouteClaim(
    shellRouteClaimTable(),
    browserLocation.pathname
  );
  const remoteConfig = active
    ? window.__FORGE_SHELL_CONFIG__?.remotes?.[active.remote.name]
    : undefined;
  React.useEffect(() => {
    if (remoteConfig) {
      void loadRemoteMount(remoteConfig);
    }
  }, [remoteConfig]);

  /*
    Analytics bootstraps at the surface, not inside a route: a remote page is
    not wrapped in `RequireViewer`, so nothing under it would otherwise ask who
    the viewer is, and the shell owns tracking-script consent and identity for
    every remote. `RequireOnboarded` and `RequireViewer` still run their own
    `useViewer` — this is the same query and Apollo's cache serves it without a
    second request.
  */
  const {viewer, loading} = useViewer({skip: false});
  useAnalyticsBootstrap({viewer, loading});

  // The CLI's pages are a standalone dialog: no header, no remote.
  if (CLI_LOGIN_PATHS.includes(browserLocation.pathname)) {
    return (
      <ShellErrorBoundary resetKey={browserLocation.key}>
        <CLILoginRoutes origins={origins} />
      </ShellErrorBoundary>
    );
  }

  return (
    <div className={styles.shell}>
      <ShellHeader />
      <main
        className={styles.content}
        style={active?.remote.name === 'wandb' ? undefined : ARIA_INSET_STYLE}>
        <ShellErrorBoundary resetKey={browserLocation.key}>
          <Routes>
            <Route
              path="/login"
              element={
                <IdentityRedirect
                  origins={origins}
                  path="/login"
                  fallback={<ShellNotFound />}
                />
              }
            />
            <Route
              path="/signup"
              element={
                <IdentityRedirect
                  origins={origins}
                  path="/signup"
                  fallback={<ShellNotFound />}
                />
              }
            />
            <Route
              path="*"
              element={
                <RequireOnboarded origins={origins}>
                  {active ? (
                    <ClaimedPage active={active} />
                  ) : (
                    <Routes>
                      <Route
                        index
                        element={
                          <RequireViewer origins={origins}>
                            <HomePage />
                          </RequireViewer>
                        }
                      />
                      <Route path="*" element={<ShellNotFound />} />
                    </Routes>
                  )}
                </RequireOnboarded>
              }
            />
          </Routes>
        </ShellErrorBoundary>
      </main>
    </div>
  );
};

export const App = ({
  origin = window.location.origin,
}: {origin?: string} = {}) => {
  const origins = window.__FORGE_SHELL_CONFIG__?.origins;
  const surface = resolveSurface(origin, origins);

  /*
    Above the surface switch, so one mount covers both origins: `main.tsx` puts
    the router around all of `App`, so `useLocation` works here. Needs neither
    Apollo nor the account context, unlike `useAnalyticsBootstrap`.
  */
  usePageViews({surface});

  switch (surface) {
    case 'identity':
      return (
        <React.Suspense
          fallback={<ShellLoader label="Loading session" fullPage />}>
          <IdentitySurface origins={origins} />
        </React.Suspense>
      );
    case 'forge':
    default:
      return (
        <UrlAccountBroadcastProvider>
          <AccountProvider>
            <ColorSchemeSync />
            <ShellHeaderProvider>
              <AriaProvider>
                <ForgeSurface origins={origins} />
              </AriaProvider>
            </ShellHeaderProvider>
          </AccountProvider>
        </UrlAccountBroadcastProvider>
      );
  }
};
